import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormSwitch } from 'components/form-fields';
import { NotificationInline } from 'components/notifications';
import { ShowHide } from 'components/layout';
import { LicenceCheckingSharedData } from 'pages/licence-checking/shared';

export const licenceCheckingRequiredAction = "LicenceChecking:Manage";

export const noLicenceCheckingAccess = {
    allowPurchaseCredits: false,
    performManualChecks: false,
    allowAccessGreyFleet: false
};

const loadFailedInlineMessage = "Licence checking access is currently unavailable. Refresh the page to try again.";
const defaultFunction = () => { };

const sharedPermissions = LicenceCheckingSharedData.getPermissions();

function getPermissionDescription(role) {
    const sharedPermission = sharedPermissions.find((permission) => permission.role === role);

    if (sharedPermission == null) {
        return null;
    }

    return sharedPermission.description;
}

const permissionFields = [
    { id: "allow-purchase-credits", field: "allowPurchaseCredits", heading: "Purchase licence checking credits:", role: "Purchase Credits" },
    { id: "perform-manual-checks", field: "performManualChecks", heading: "Perform licence checks:", role: "Perform Checks" },
    { id: "allow-access-grey-fleet", field: "allowAccessGreyFleet", heading: "Manage grey fleet:", role: "Access Grey Fleet" }
].map((permissionField) => {
    return { ...permissionField, description: getPermissionDescription(permissionField.role) };
});

export function hasLicenceCheckingPermissionChanges(currentPermissions, originalPermissions) {
    return permissionFields.some((permissionField) => {
        return currentPermissions[permissionField.field] !== originalPermissions[permissionField.field];
    });
}

export function UserLicenceCheckingPermissions(
    {
        isLicenceCheckingUser = false,
        permissions = noLicenceCheckingAccess,
        loading = false,
        disabled = false,
        loadFailed = false,
        onIsLicenceCheckingUserChange = defaultFunction,
        onPermissionChange = defaultFunction
    }
) {

    return (
        <div className="row">
            <ShowHide
                evaluator={loadFailed}
                show={(
                    <NotificationInline
                        allowClose={false}
                        show
                        text={loadFailedInlineMessage}
                        intent="danger"
                    />
                )}
                hide={(
                    <Fragment>
                        <FormSwitch
                            id="is-licence-checking-user"
                            headingText="Manage licence checking:"
                            helperText="Allows the user to view licence checking data. Additional permissions can be configured below once this is turned on."
                            checked={isLicenceCheckingUser}
                            loading={loading}
                            disabled={disabled}
                            onChange={onIsLicenceCheckingUserChange}
                        />

                        <ShowHide
                            evaluator={isLicenceCheckingUser}
                            show={(
                                <Fragment>
                                    {permissionFields.map((permissionField) => (
                                        <FormSwitch
                                            key={permissionField.field}
                                            id={permissionField.id}
                                            headingText={permissionField.heading}
                                            helperText={permissionField.description}
                                            checked={permissions[permissionField.field]}
                                            loading={loading}
                                            disabled={disabled}
                                            onChange={(event) => onPermissionChange(permissionField.field, event)}
                                        />
                                    ))}
                                </Fragment>
                            )}
                        />
                    </Fragment>
                )}
            />
        </div>
    );
}

UserLicenceCheckingPermissions.propTypes = {
    isLicenceCheckingUser: PropTypes.bool,
    permissions: PropTypes.shape({
        allowPurchaseCredits: PropTypes.bool,
        performManualChecks: PropTypes.bool,
        allowAccessGreyFleet: PropTypes.bool
    }),
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    loadFailed: PropTypes.bool,
    onIsLicenceCheckingUserChange: PropTypes.func,
    onPermissionChange: PropTypes.func
};
